import { Link } from '@tanstack/react-router';
import { hero } from '@/lib/marketing-data';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play, Sparkles } from 'lucide-react';

export function Hero() {
  return (
    <section className="relative overflow-hidden pt-24 pb-16 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32">
      {/* Background gradient */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          {/* Badge */}
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border bg-background/50 px-4 py-1.5 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{hero.badge}</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            {hero.headline.split('\n').map((line, i) => (
              <span key={i} className="block">
                {i === 1 ? (
                  <span className="text-primary">{line}</span>
                ) : (
                  line
                )}
              </span>
            ))}
          </h1>

          {/* Subheadline */}
          <p className="mt-6 text-lg text-muted-foreground sm:text-xl md:text-2xl max-w-3xl mx-auto">
            {hero.subheadline}
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={hero.primaryCTA.href}>
              <Button size="lg" className="w-full sm:w-auto gap-2">
                {hero.primaryCTA.text}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to={hero.secondaryCTA.href}>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto gap-2"
              >
                <Play className="h-4 w-4" />
                {hero.secondaryCTA.text}
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-3 gap-8 border-t border-border pt-8">
            {hero.stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl font-bold text-primary sm:text-4xl">
                  {stat.value}
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Product Preview */}
        <div className="mt-16 sm:mt-24">
          <div className="relative mx-auto max-w-6xl">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-2xl blur-2xl" />
            <div className="relative rounded-xl border bg-card shadow-2xl overflow-hidden">
              <div className="flex items-center gap-2 border-b bg-muted/50 px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <div className="h-3 w-3 rounded-full bg-yellow-500" />
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                </div>
                <span className="text-sm text-muted-foreground ml-2">
                  forgenetwork.pro/dashboard
                </span>
              </div>
              <div className="aspect-[16/9] bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-8">
                <div className="w-full h-full rounded-lg bg-slate-950/50 border border-slate-700/50 p-4 space-y-3">
                  {/* Simulated dashboard UI */}
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded bg-primary/20 flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-primary" />
                    </div>
                    <div className="h-4 w-32 bg-slate-700/50 rounded" />
                    <div className="ml-auto flex gap-2">
                      <div className="h-6 w-20 bg-emerald-500/20 rounded text-xs text-emerald-400 flex items-center justify-center">Analyzed</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div className="text-xs text-slate-400 mb-1">Features Found</div>
                      <div className="text-xl font-bold text-white">147</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div className="text-xs text-slate-400 mb-1">Tech Debt Items</div>
                      <div className="text-xl font-bold text-amber-400">23</div>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/30">
                      <div className="text-xs text-slate-400 mb-1">Security Score</div>
                      <div className="text-xl font-bold text-emerald-400">94%</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 w-full bg-slate-700/30 rounded" />
                    <div className="h-3 w-4/5 bg-slate-700/30 rounded" />
                    <div className="h-3 w-3/4 bg-slate-700/30 rounded" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
